import { useState } from 'react';
import clsx from 'clsx';
import styles from './FaceCard.module.css';

// ---------- Helper functions ----------

/**
 * Calculate CSS crop style to show face region from preview image.
 * Reuses the same math as Training.jsx face thumbnails.
 */
function computeCropStyle(face, containerSize) {
  const bbox = face.bbox || [];
  const imgW = face.crop_width;
  const imgH = face.crop_height;
  if (bbox.length !== 4 || !imgW || !imgH) return null;

  const [x1, y1, x2, y2] = bbox;
  const faceW = x2 - x1;
  const faceH = y2 - y1;
  const faceCenterX = x1 + faceW / 2;
  const faceCenterY = y1 + faceH / 2;

  const padding = 1.3;
  const scale = containerSize / (Math.max(faceW, faceH) * padding);

  return {
    width: imgW * scale,
    height: imgH * scale,
    position: 'absolute',
    left: (containerSize / 2) - (faceCenterX * scale),
    top: (containerSize / 2) - (faceCenterY * scale),
  };
}

/**
 * Calculate match score from embedding distance
 */
function calculateScore(distance) {
  if (distance == null) return null;
  return Math.max(0, 1 - distance);
}

/**
 * Get person name from face data
 */
function getPersonName(face) {
  return (
    face.person_name ||
    face.person_display ||
    (face.person ? face.person.display_name : null)
  );
}

/**
 * Build title string from person name and percentage
 */
function buildTitle(personName, pct) {
  if (personName) {
    return `${personName} (${pct}% match)`;
  }
  if (pct != null) {
    return `${pct}% match`;
  }
  return undefined;
}

// ---------- Sub-components ----------

/**
 * Image container with CSS-cropped preview or fallback
 */
function ImageContent({ resolvedUrl, face, variant, containerSize, onError }) {
  const cropStyle = resolvedUrl ? computeCropStyle(face, containerSize) : null;

  if (resolvedUrl && cropStyle) {
    return (
      <img
        src={resolvedUrl}
        alt={face.id || 'face'}
        onError={onError}
        style={cropStyle}
        draggable={false}
      />
    );
  }

  if (resolvedUrl) {
    // No bbox data - show preview as object-fit cover fallback
    return (
      <img
        src={resolvedUrl}
        alt={face.id || 'face'}
        onError={onError}
        className={styles.image}
      />
    );
  }

  return (
    <div className={styles.fallback}>
      {variant === 'compact' ? face.id?.slice(0, 4) : '?'}
    </div>
  );
}

/**
 * Info section with score and optional name
 */
function InfoSection({ pct, variant, personName }) {
  return (
    <div className={styles.info}>
      {pct != null && (
        <span className={styles.score}>{pct}%</span>
      )}
      {variant === 'detailed' && personName && (
        <span className={styles.name}>{personName}</span>
      )}
    </div>
  );
}

// ---------- Main component ----------

/**
 * FaceCard - Unified face thumbnail component.
 *
 * Consolidates NeighborCard and ModalNeighborItem into a single component
 * following DRY principle. Can display face thumbnails with match scores
 * and optional person names.
 *
 * @param {Object} props
 * @param {Object} props.face - Face data object
 * @param {string} [props.face.thumbnail] - Face thumbnail URL
 * @param {number} [props.face.distance] - Embedding distance (0-1)
 * @param {string} [props.face.person_name] - Person display name
 * @param {'compact' | 'detailed'} [props.variant='compact'] - Display variant
 * @param {Function} [props.resolveUrl] - URL resolver function
 * @param {Function} [props.onClick] - Click handler
 * @param {boolean} [props.selected] - Whether card is selected
 * @param {string} [props.className] - Additional CSS classes
 *
 * @example
 * // Compact (grid view, just score badge)
 * <FaceCard face={neighbor} variant="compact" />
 *
 * @example
 * // Detailed (modal view, score + name)
 * <FaceCard face={neighbor} variant="detailed" resolveUrl={resolveUrl} />
 */
export default function FaceCard({
  face,
  variant = 'compact',
  resolveUrl = (url) => url,
  onClick,
  selected = false,
  className,
}) {
  const [hasError, setHasError] = useState(false);

  // Get display values
  const previewUrl = face.preview_url;
  const score = calculateScore(face.distance);
  const pct = score != null ? Math.round(score * 100) : null;
  const personName = getPersonName(face);
  const containerSize = variant === 'compact' ? 48 : 40;

  // Hide card if image failed to load
  if (hasError) return null;

  const resolvedUrl = previewUrl ? resolveUrl(previewUrl) : null;
  const title = buildTitle(personName, pct);

  // Handle keyboard interaction
  const handleKeyDown = onClick
    ? (e) => e.key === 'Enter' && onClick(e)
    : undefined;

  return (
    <div
      className={clsx(
        styles.card,
        styles[variant],
        selected && styles.selected,
        onClick && styles.clickable,
        className
      )}
      onClick={onClick}
      title={title}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.imageContainer}>
        <ImageContent
          resolvedUrl={resolvedUrl}
          face={face}
          variant={variant}
          containerSize={containerSize}
          onError={() => setHasError(true)}
        />
      </div>

      <InfoSection
        pct={pct}
        variant={variant}
        personName={personName}
      />
    </div>
  );
}
